import React, { useMemo, useState } from 'react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import type { FamilyEnrollmentRow } from './types';
import {
  describeEnrollment,
  phaseFor,
  phasesFor,
  studentsLine,
  type Phase,
  type Tone,
} from './enrollmentPhases';
import './EnrollmentBoard.css';

const KIND_LABELS: Record<string, string> = {
  RE_ENROLLMENT: 'Re-enroll',
  NEW_ADMISSION: 'New',
};

const DOT_CLASSES: Record<Tone, string> = {
  muted: 'bg-slate-400',
  neutral: 'bg-slate-900',
  warning: 'bg-orange-600',
  danger: 'bg-red-600',
  success: 'bg-green-600',
};

const TEXT_CLASSES: Record<Tone, string> = {
  muted: 'text-muted-foreground',
  neutral: 'text-foreground',
  warning: 'text-orange-700',
  danger: 'text-red-600',
  success: 'text-foreground',
};

const COUNT_CLASSES: Record<Phase, string> = {
  setup: 'bg-secondary text-secondary-foreground',
  open: 'bg-secondary text-secondary-foreground',
  in_review: 'bg-orange-100 text-orange-700',
  offered: 'bg-secondary text-secondary-foreground',
  complete: 'bg-green-100 text-green-700',
  closed: 'bg-secondary text-secondary-foreground',
};

/*
 * Approved families are the one column that only grows, and by spring it is
 * long enough to push every column that still needs work off the screen. It
 * keeps the four most recent on the board and puts the rest one click away:
 * the count in the header is still the real total.
 */
const COLLAPSED_PHASES: Partial<Record<Phase, number>> = { complete: 4 };

interface EnrollmentBoardProps {
  enrollments: FamilyEnrollmentRow[];
  selectedId?: string | null;
  onSelect: (enrollment: FamilyEnrollmentRow) => void;
  phaseFilter?: Phase | null;
  onClearPhaseFilter?: () => void;
  reenrollmentOpen?: boolean | null;
  openEnrollmentOpen?: boolean | null;
  /** With the checklist on, every year is placed and described by its access. */
  checklistEnrollmentOn?: boolean;
}

/**
 * Family enrollments as a five-phase board. Cards are buttons; selecting one
 * opens the family drawer, which is where every action lives.
 */
export function EnrollmentBoard({
  enrollments,
  selectedId,
  onSelect,
  phaseFilter,
  onClearPhaseFilter,
  reenrollmentOpen,
  openEnrollmentOpen,
  checklistEnrollmentOn = false,
}: EnrollmentBoardProps) {
  const byPhase = useMemo(() => {
    const groups: Record<Phase, FamilyEnrollmentRow[]> = {
      setup: [], open: [], in_review: [], offered: [], complete: [], closed: [],
    };
    for (const row of enrollments) {
      groups[phaseFor(row, checklistEnrollmentOn)].push(row);
    }
    return groups;
  }, [enrollments, checklistEnrollmentOn]);

  const phases = phasesFor(checklistEnrollmentOn);
  // A filter for a column this school's board doesn't have shows the whole board.
  const filter = phases.some((phase) => phase.key === phaseFilter) ? phaseFilter : null;
  const visiblePhases = phases.filter((phase) => {
    if (filter) return phase.key === filter;
    // Declined families only take up a column when there are some.
    return phase.key !== 'closed' || byPhase.closed.length > 0;
  });

  return (
    <div className="space-y-3" data-testid="enrollment-board">
      {filter && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          Showing only {phases.find((p) => p.key === filter)?.label.toLowerCase()}.
          <Button variant="link" size="sm" className="h-auto p-0" onClick={onClearPhaseFilter}>
            Show all phases
          </Button>
        </div>
      )}
      {/* Columns never squeeze below a readable width; the board scrolls sideways instead. */}
      <div
        className="enrollment-board pb-2"
        style={{ '--board-columns': visiblePhases.length } as React.CSSProperties}
      >
        {visiblePhases.map((phase) => {
          const rows = byPhase[phase.key];
          return (
            <section key={phase.key} aria-label={phase.label} className="flex min-w-0 flex-col gap-2">
              <header className="px-1 pb-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{phase.label}</span>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${COUNT_CLASSES[phase.key]}`}>
                    {rows.length}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{phase.hint}</p>
              </header>
              <PhaseCards
                rows={rows}
                limit={COLLAPSED_PHASES[phase.key]}
                phaseLabel={phase.label}
                selectedId={selectedId}
                onSelect={onSelect}
                reenrollmentOpen={reenrollmentOpen}
                openEnrollmentOpen={openEnrollmentOpen}
                checklistEnrollmentOn={checklistEnrollmentOn}
              />
            </section>
          );
        })}
      </div>
    </div>
  );
}

interface PhaseCardsProps {
  rows: FamilyEnrollmentRow[];
  /** How many cards to show before the rest go behind a button. */
  limit?: number;
  phaseLabel: string;
  selectedId?: string | null;
  onSelect: (enrollment: FamilyEnrollmentRow) => void;
  reenrollmentOpen?: boolean | null;
  openEnrollmentOpen?: boolean | null;
  checklistEnrollmentOn: boolean;
}

function PhaseCards({
  rows, limit, phaseLabel, selectedId, onSelect, reenrollmentOpen, openEnrollmentOpen, checklistEnrollmentOn,
}: PhaseCardsProps) {
  const [expanded, setExpanded] = useState(false);
  const collapsed = limit !== undefined && !expanded && rows.length > limit;
  const visible = collapsed ? rows.slice(0, limit) : rows;
  const hidden = rows.length - visible.length;

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-muted p-2 md:min-h-[320px]">
        {rows.length === 0 && (
          <p className="px-1 py-2 text-center text-xs text-muted-foreground md:py-3">No families here</p>
        )}
        {visible.map((row) => {
          const status = describeEnrollment(row, { reenrollmentOpen, openEnrollmentOpen }, undefined, checklistEnrollmentOn);
          const selected = row.id === selectedId;
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => onSelect(row)}
              aria-pressed={selected}
              className={`flex flex-col gap-1.5 rounded-md border bg-card p-3 text-left shadow-sm transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                selected ? 'border-primary ring-1 ring-primary' : 'border-border'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-semibold">{row.family.name}</span>
                <Badge variant="outline" className="shrink-0">
                  {KIND_LABELS[row.enrollmentKind] ?? row.enrollmentKind}
                </Badge>
              </div>
              <span className="truncate text-xs text-muted-foreground">{studentsLine(row)}</span>
              <span className={`flex items-center gap-1.5 text-xs ${TEXT_CLASSES[status.tone]}`}>
                <span className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLASSES[status.tone]}`} />
                <span className="truncate">{status.text}</span>
              </span>
              {row.state === 'NEEDS_REVIEW' && (row.reviewReasons ?? []).length > 0 && (
                <span className="rounded bg-orange-50 px-2 py-1 text-xs text-orange-800">
                  {(row.reviewReasons ?? []).join(' · ')}
                </span>
              )}
            </button>
          );
        })}
        {limit !== undefined && rows.length > limit && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded(!expanded)}
            aria-label={collapsed ? `Show all ${rows.length} families in ${phaseLabel}` : `Show fewer families in ${phaseLabel}`}
          >
            {collapsed ? `Show ${hidden} more` : 'Show fewer'}
          </Button>
        )}
      </div>
  );
}

export default EnrollmentBoard;
